/**
 * SegmentedControl — iOS-style sliding thumb selector (ios-fluidity-spec §5.1).
 *
 * Used for binary/ternary enum pickers: 12h/24h, price/change, league picker.
 * Replaces bare `<select>` elements in add-forms and detail sheets.
 *
 * A11y:
 *   - Each segment is a `<button role="radio">` with `aria-checked`.
 *   - Keyboard: Arrow keys cycle through segments; Space/Enter selects.
 *   - Min 44px touch target via wrapper height.
 *   - Focus ring via :focus-visible (global style).
 *
 * Animation:
 *   - Sliding thumb uses --spring-settle + --dur-settle for smooth transition.
 *   - Reduced-motion: instant thumb jump (transition: none).
 *
 * Immutability: pure controlled component — no internal selected-state mutation.
 */
import React, { useRef, useLayoutEffect, useState } from 'react';

export interface SegmentOption {
  readonly value: string;
  readonly label: string;
}

interface SegmentedControlProps {
  /** All available options. */
  readonly options: readonly SegmentOption[];
  /** Currently selected value. */
  readonly value: string;
  /** Called when the user selects a different segment. */
  readonly onChange: (value: string) => void;
  /** aria-label for the control group. */
  readonly ariaLabel: string;
}

/**
 * Returns true if prefers-reduced-motion is active.
 * Pure function — safe to call on every render.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function SegmentedControl({
  options,
  value,
  onChange,
  ariaLabel,
}: SegmentedControlProps): React.ReactElement {
  const trackRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Thumb position: left offset + width in px.
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);

  const selectedIndex = options.findIndex((o) => o.value === value);

  // Update thumb position whenever selected index or layout changes.
  useLayoutEffect(() => {
    const track = trackRef.current;
    const btn = buttonRefs.current[selectedIndex];
    if (!track || !btn) return;
    const trackRect = track.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    setThumb({
      left: btnRect.left - trackRect.left,
      width: btnRect.width,
    });
  }, [selectedIndex, options.length]);

  // Keyboard navigation: left/right arrows cycle through options.
  function handleKeyDown(e: React.KeyboardEvent, index: number): void {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (index + 1) % options.length;
      onChange(options[next]!.value);
      buttonRefs.current[next]?.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (index - 1 + options.length) % options.length;
      onChange(options[prev]!.value);
      buttonRefs.current[prev]?.focus();
    }
  }

  return (
    // 44px min-height wrapper for touch target (§5.3 / WCAG 2.5.5).
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      style={{ minHeight: 44, display: 'flex', alignItems: 'center' }}
    >
      {/* Track */}
      <div
        ref={trackRef}
        style={{
          position: 'relative',
          display: 'flex',
          background: 'var(--color-bg-input)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          padding: 2,
          height: 36,
          width: 'fit-content',
        }}
      >
        {/* Sliding thumb (§5.1) */}
        {thumb && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 2,
              left: thumb.left,
              width: thumb.width,
              height: 'calc(100% - 4px)',
              background: 'var(--color-bg-tile)',
              borderRadius: 8,
              boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
              transition: prefersReducedMotion()
                ? 'none'
                : `left var(--dur-settle, 240ms) var(--spring-settle, ease-out),
                   width var(--dur-settle, 240ms) var(--spring-settle, ease-out)`,
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        )}

        {/* Segment buttons */}
        {options.map((option, index) => {
          const isSelected = option.value === value;
          return (
            <button
              key={option.value}
              ref={(el) => { buttonRefs.current[index] = el; }}
              type="button"
              role="radio"
              aria-checked={isSelected}
              tabIndex={isSelected ? 0 : -1}
              onClick={() => onChange(option.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              style={{
                position: 'relative',
                zIndex: 1,
                background: 'none',
                border: 'none',
                borderRadius: 8,
                fontSize: 'var(--text-label)',
                fontWeight: isSelected ? 500 : 400,
                color: isSelected ? 'var(--color-text-primary)' : 'var(--color-text-dim)',
                padding: '0 var(--space-3)',
                minWidth: 60,
                height: '100%',
                lineHeight: '32px',
                textAlign: 'center',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                // Smooth text color transition.
                transition: prefersReducedMotion()
                  ? 'none'
                  : 'color var(--dur-settle, 240ms) ease-out, font-weight 0ms',
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
